import { useState } from 'react';
import { createPortal } from 'react-dom';
import { changeResignationDocument, type ResignationDocument } from '@urbanik/api-client';
import {
  CanonicalModal,
  Button,
  TextareaField,
  FileUpload,
  InfoPanel,
  FormActions,
} from '@urbanik/ui';
import { Trash2, Replace } from 'lucide-react';
import { apiBase } from './api-base';
export function ResignationDocumentChange({
  memberId,
  historyId,
  document,
  mode,
  onClose,
  onChanged,
}: {
  memberId: string;
  historyId: string;
  document: ResignationDocument;
  mode: 'replace' | 'delete';
  onClose: () => void;
  onChanged: (documents: ResignationDocument[]) => void;
}) {
  const [reason, setReason] = useState(''),
    [file, setFile] = useState<File | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const remove = mode === 'delete';
  const save = async () => {
    if (busy || reason.trim().length < 10 || (!remove && !file)) return;
    setBusy(true);
    setError('');
    try {
      const result = await changeResignationDocument(
        apiBase,
        memberId,
        historyId,
        document.id,
        reason,
        remove ? undefined : file!,
      );
      onChanged(result.documents);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos procesar la constancia.');
    } finally {
      setBusy(false);
    }
  };
  return createPortal(
    <CanonicalModal
      open
      title={remove ? 'Eliminar constancia' : 'Reemplazar constancia'}
      description={document.fileName}
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      <InfoPanel tone="warning">
        {remove
          ? 'El PDF se eliminará definitivamente del almacenamiento activo.'
          : 'El nuevo PDF sustituirá al anterior, que se retirará del almacenamiento activo.'}{' '}
        No podrás recuperar el archivo anterior desde la app. Se conservará únicamente la
        trazabilidad de la operación; los respaldos existentes no se modifican.
      </InfoPanel>
      {error ? <InfoPanel tone="danger">{error}</InfoPanel> : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        {!remove ? (
          <FileUpload
            label="Nuevo PDF de constancia"
            file={file}
            onFileChange={setFile}
            onError={setError}
            disabled={busy}
            helpText="PDF, máximo 20 MB. El original se conserva si falla el reemplazo."
          />
        ) : null}
        <TextareaField
          label="Motivo de la operación"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          minLength={10}
          maxLength={500}
          required
          disabled={busy}
        />
        <FormActions>
          <Button type="button" disabled={busy} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            icon={remove ? <Trash2 aria-hidden="true" /> : <Replace aria-hidden="true" />}
            disabled={busy || reason.trim().length < 10 || (!remove && !file)}
          >
            {busy
              ? 'Procesando…'
              : remove
                ? 'Confirmar eliminación definitiva'
                : 'Confirmar reemplazo'}
          </Button>
        </FormActions>
      </form>
    </CanonicalModal>,
    window.document.body,
  );
}
